// Artefacts de Vanta AI : le modèle écrit
//   <artifact id="…" type="html|react|svg|mermaid|markdown|code|three|project" title="…" language="…"> … </artifact>
// Un « project » contient plusieurs fichiers, chacun précédé d'une ligne « === chemin/fichier.ext === »,
// et se télécharge en .zip.
// La réponse est découpée en morceaux de texte et en artefacts (même pendant
// l'écriture : un artefact pas encore fermé est « en cours »). Les aperçus
// tournent dans une iframe isolée (sandbox sans accès au site).

const OPEN = /<artifact\b([^>]*)>/i;
const CLOSE = /<\/artifact>/i;
export const ARTIFACT_TYPES = ["html", "react", "svg", "mermaid", "markdown", "code", "three", "project"];

function attrs(raw) {
  const out = {};
  raw.replace(/([\w-]+)\s*=\s*"([^"]*)"/g, (_, k, v) => { out[k] = v; return ""; });
  return out;
}

/** Découpe un texte : [{type:"text", text}, {type:"artifact", id, kind, title, language, content, complete}] */
export function splitArtifacts(text) {
  const parts = [];
  let rest = text || "";
  for (;;) {
    const open = rest.match(OPEN);
    if (!open) break;
    const before = rest.slice(0, open.index);
    if (before.trim()) parts.push({ type: "text", text: before });
    const after = rest.slice(open.index + open[0].length);
    const close = after.match(CLOSE);
    const a = attrs(open[1]);
    const kind = ARTIFACT_TYPES.includes(a.type) ? a.type : "code";
    const content = (close ? after.slice(0, close.index) : after).replace(/^\n/, "").replace(/\n$/, "");
    parts.push({ type: "artifact", id: a.id || `artefact-${parts.length}`, kind, title: a.title || "Artefact", language: a.language || "", content, complete: !!close });
    if (!close) { rest = ""; break; }
    rest = after.slice(close.index + close[0].length);
  }
  // Balise d'ouverture pas encore entièrement reçue : on ne l'affiche pas.
  const partial = rest.lastIndexOf("<artifact");
  if (partial >= 0 && !rest.slice(partial).includes(">")) rest = rest.slice(0, partial);
  if (rest.trim()) parts.push({ type: "text", text: rest });
  return parts;
}

/** Tous les artefacts d'une conversation, regroupés par id (versions successives). */
export function collectArtifacts(messages) {
  const map = new Map();
  messages.forEach((m) => {
    if (m.role !== "assistant") return;
    (m.blocks || []).forEach((b) => {
      if (b.type !== "text") return;
      splitArtifacts(b.text).forEach((p) => {
        if (p.type !== "artifact") return;
        const list = map.get(p.id) || [];
        list.push({ ...p, messageId: m.id });
        map.set(p.id, list);
      });
    });
  });
  return map;
}

export const KIND_LABELS = { html: "Page web", react: "Composant React", svg: "Image SVG", mermaid: "Diagramme", markdown: "Document", code: "Code", three: "Scène 3D", project: "Projet (.zip)" };

const FILE_LINE = /^\s*={3,}\s*(.+?)\s*={3,}\s*$/;

/** Fichiers d'un projet : [{ path, content }] (chemins nettoyés : pas de « .. » ni de chemin absolu). */
export function projectFiles(content) {
  const files = [];
  let current = null;
  (content || "").split("\n").forEach((line) => {
    const m = line.match(FILE_LINE);
    if (m) {
      const path = m[1].replace(/\\/g, "/").split("/").filter((x) => x && x !== "." && x !== "..").join("/");
      current = { path: path || `fichier-${files.length + 1}.txt`, lines: [] };
      files.push(current);
    } else if (current) {
      current.lines.push(line);
    }
  });
  return files.map((f) => ({ path: f.path, content: f.lines.join("\n").replace(/^\n+/, "").replace(/\n*$/, "\n") }));
}

const EXT = { html: "html", react: "jsx", svg: "svg", mermaid: "mmd", markdown: "md", three: "js", project: "zip" };
const LANG_EXT = { python: "py", py: "py", javascript: "js", js: "js", typescript: "ts", ts: "ts", lua: "lua", bash: "sh", sh: "sh", json: "json", css: "css", html: "html", java: "java", csharp: "cs", cpp: "cpp", c: "c", go: "go", rust: "rs", php: "php", sql: "sql", yaml: "yml" };

export function baseName(artifact) {
  return (artifact.title || "artefact").normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w-]+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "artefact";
}

export function fileName(artifact) {
  const base = (artifact.title || "artefact").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^\w-]+/g, "-").replace(/^-|-$/g, "").toLowerCase() || "artefact";
  const ext = artifact.kind === "code" ? (LANG_EXT[(artifact.language || "").toLowerCase()] || "txt") : EXT[artifact.kind];
  return `${base}.${ext}`;
}

export const previewable = (kind) => ["html", "react", "svg", "mermaid", "three"].includes(kind);

const BASE_STYLE = "<style>html,body{margin:0;min-height:100%;background:#fff;font-family:system-ui,sans-serif}</style>";

function reactDoc(code) {
  let src = code.replace(/^\s*import\s[^;\n]*from\s+['"]react(-dom)?(\/client)?['"];?\s*$/gm, "").replace(/^\s*import\s+['"][^'"]+['"];?\s*$/gm, "");
  let name = "App";
  const named = src.match(/export\s+default\s+function\s+([A-Za-z_]\w*)/);
  if (named) { name = named[1]; src = src.replace(/export\s+default\s+function/, "function"); }
  else {
    const expr = src.match(/export\s+default\s+([A-Za-z_]\w*)\s*;?/);
    if (expr) { name = expr[1]; src = src.replace(expr[0], ""); }
  }
  const json = JSON.stringify(`const { useState, useEffect, useRef, useMemo, useCallback, useReducer, Fragment } = React;\n${src}\nReactDOM.createRoot(document.getElementById("root")).render(React.createElement(${name}));`);
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">${BASE_STYLE}
<script src="https://cdn.tailwindcss.com/3.4.16"></script>
<script crossorigin src="https://cdnjs.cloudflare.com/ajax/libs/react/18.3.1/umd/react.production.min.js"></script>
<script crossorigin src="https://cdnjs.cloudflare.com/ajax/libs/react-dom/18.3.1/umd/react-dom.production.min.js"></script>
<script src="https://cdnjs.cloudflare.com/ajax/libs/babel-standalone/7.26.4/babel.min.js"></script></head>
<body><div id="root"></div><script>
try { const out = Babel.transform(${json}, { presets: ["react"] }).code; new Function(out)(); }
catch (e) { document.body.innerHTML = '<pre style="color:#b91c1c;padding:16px;white-space:pre-wrap">' + String(e.message).replace(/</g, "&lt;") + "</pre>"; }
</script></body></html>`;
}

function threeDoc(code) {
  return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;height:100%;overflow:hidden;background:#111}</style>
<script src="https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js"></script>
<script src="https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js"></script></head><body><script>
const scene = new THREE.Scene(); scene.background = new THREE.Color(0x15151c);
const camera = new THREE.PerspectiveCamera(60, innerWidth / innerHeight, 0.1, 1000); camera.position.set(4, 3, 6);
const renderer = new THREE.WebGLRenderer({ antialias: true }); renderer.setSize(innerWidth, innerHeight); renderer.setPixelRatio(devicePixelRatio);
document.body.appendChild(renderer.domElement);
const controls = new THREE.OrbitControls(camera, renderer.domElement); controls.enableDamping = true;
scene.add(new THREE.AmbientLight(0xffffff, 0.5)); const sun = new THREE.DirectionalLight(0xffffff, 0.9); sun.position.set(5, 10, 7); scene.add(sun);
const updates = []; const onFrame = (fn) => updates.push(fn);
addEventListener("resize", () => { camera.aspect = innerWidth / innerHeight; camera.updateProjectionMatrix(); renderer.setSize(innerWidth, innerHeight); });
try { (function (THREE, scene, camera, renderer, controls, onFrame) { ${code}\n })(THREE, scene, camera, renderer, controls, onFrame); }
catch (e) { document.body.innerHTML = '<pre style="color:#f87171;padding:16px;white-space:pre-wrap">' + String(e.message).replace(/</g, "&lt;") + "</pre>"; }
(function loop(t) { requestAnimationFrame(loop); updates.forEach((fn) => fn(t)); controls.update(); renderer.render(scene, camera); })(0);
</script></body></html>`;
}

export function modelViewerDoc(url) {
  return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;height:100%;overflow:hidden;background:#15151c;color:#aaa;font-family:system-ui}</style>
<script src="https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.min.js"></script>
<script src="https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/controls/OrbitControls.js"></script>
<script src="https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js"></script></head><body><script>
const scene = new THREE.Scene(); scene.background = new THREE.Color(0x15151c);
const camera = new THREE.PerspectiveCamera(50, innerWidth / innerHeight, 0.01, 1000);
const renderer = new THREE.WebGLRenderer({ antialias: true }); renderer.setSize(innerWidth, innerHeight); renderer.setPixelRatio(devicePixelRatio);
renderer.outputEncoding = THREE.sRGBEncoding; document.body.appendChild(renderer.domElement);
scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1)); const sun = new THREE.DirectionalLight(0xffffff, 0.8); sun.position.set(3, 6, 4); scene.add(sun);
const controls = new THREE.OrbitControls(camera, renderer.domElement); controls.enableDamping = true;
new THREE.GLTFLoader().load(${JSON.stringify(url)}, (g) => {
  scene.add(g.scene); const box = new THREE.Box3().setFromObject(g.scene); const size = box.getSize(new THREE.Vector3()).length() || 1;
  const center = box.getCenter(new THREE.Vector3()); controls.target.copy(center); camera.position.copy(center).add(new THREE.Vector3(size * 0.6, size * 0.45, size * 0.8));
}, undefined, () => { document.body.innerHTML = "<p style='padding:16px'>Modèle 3D illisible.</p>"; });
addEventListener("resize", () => { camera.aspect = innerWidth / innerHeight; camera.updateProjectionMatrix(); renderer.setSize(innerWidth, innerHeight); });
(function loop() { requestAnimationFrame(loop); controls.update(); renderer.render(scene, camera); })();
</script></body></html>`;
}

/** Page HTML complète de l'aperçu (iframe srcdoc). */
export function previewDoc(artifact) {
  const { kind, content } = artifact;
  if (kind === "html") return /<html[\s>]/i.test(content) ? content : `<!doctype html><html><head><meta charset="utf-8">${BASE_STYLE}</head><body>${content}</body></html>`;
  if (kind === "react") return reactDoc(content);
  if (kind === "three") return threeDoc(content);
  if (kind === "svg") return `<!doctype html><html><head><meta charset="utf-8"><style>html,body{margin:0;height:100%;display:flex;align-items:center;justify-content:center;background:#fff}svg{max-width:100%;max-height:100vh}</style></head><body>${content}</body></html>`;
  if (kind === "mermaid") {
    return `<!doctype html><html><head><meta charset="utf-8">${BASE_STYLE}<script src="https://cdnjs.cloudflare.com/ajax/libs/mermaid/10.9.1/mermaid.min.js"></script></head>
<body><pre class="mermaid" style="display:flex;justify-content:center;padding:16px">${content.replace(/</g, "&lt;")}</pre><script>mermaid.initialize({ startOnLoad: true, theme: "default" });</script></body></html>`;
  }
  return "";
}
